import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Tag } from "lucide-react";
import { labelTexts, normalizeLabelSize, parseBarcode, type LabelContent, VN_TIME_ZONE } from "@hisense/shared";
import { useSettings } from "@/api/hooks";
import { labelRef } from "@/components/domain/LabelPrintButton";
import { LabelLanguageToggle, useLabelLanguage } from "@/components/domain/LabelLanguageToggle";
import { LabelPreview } from "@/components/products/LabelPreview";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { FieldLabel, Input } from "@/components/ui/Input";
import { buildLabelDocument, labelBarcodeFits } from "@/lib/labelDocument";
import { printHtml } from "@/lib/printWindow";

export interface LotLabel {
  productCode: string;
  subRef?: string | null;
  description?: string | null;
  /** Có khi tem in từ hồ sơ sản phẩm (mô tả / màu tiếng Hoa) — khi đó hộp thoại cho chọn ngôn ngữ trên tem. */
  descriptionZh?: string | null;
  color?: string | null;
  colorZh?: string | null;
  size?: string | null;
  lotNumber: string;
}

/** Máy quét đọc lại đúng nội dung này thành đúng số lô (và đúng mã hàng nếu có) — kiểm bằng chính bộ đọc của màn quét. */
function readsBack(text: string, lotNumber: string, productCode?: string): boolean {
  try {
    const parsed = parseBarcode(text);
    return parsed.lotNumber === lotNumber && (productCode === undefined || (parsed.kind === "productLot" && parsed.productCode === productCode));
  } catch {
    return false;
  }
}

/**
 * Nội dung mã vạch của tem lô. Ưu tiên `HISENSE:<mã hàng>:<số lô>`: hai mã hàng có thể trùng số lô của nhà cung cấp, tem
 * mang cả mã thì màn quét không phải hỏi lại là hàng nào. Mã dài không vừa khổ tem (`fits`) thì lùi về `LOT:<số lô>`.
 * Trả null khi số lô có ký tự máy quét không đọc được — tem vẫn in số lô bằng chữ, nhưng không có mã vạch.
 */
export function lotBarcode(productCode: string, lotNumber: string, fits: (barcode: string) => boolean = () => true): string | null {
  const lot = lotNumber.trim();
  const code = productCode.trim();
  const full = `HISENSE:${code}:${lot}`;
  if (readsBack(full, lot, code) && fits(full)) return full;
  const short = `LOT:${lot}`;
  return readsBack(short, lot) ? short : null;
}

/**
 * In tem LOT hàng loạt (18/09/2026): mỗi lô một tem (hoặc vài tem) mang mã vạch của lô, để kho dán lên hàng rồi quét khi
 * cất, xuất, kiểm kê. Dùng đường in qua hộp thoại hệ thống như in tem sản phẩm hàng loạt — chạy trên mọi trình duyệt và in
 * được tiếng Việt. Khổ tem đọc từ Cài đặt; mã vạch không vừa khổ thì NÓI RA và không in (tem hỏng là hỏng vật tư thật).
 */
export function LotLabelsButton({ lots, disabled, size = "sm" }: { lots: LotLabel[]; disabled?: boolean; size?: "sm" | "md" }) {
  const { t } = useTranslation();
  const { data: settings, isLoading, error: settingsError } = useSettings();
  const [open, setOpen] = useState(false);
  const [copies, setCopies] = useState("1");
  const [error, setError] = useState(false);
  const [language, setLanguage] = useLabelLanguage();
  const bilingual = lots.some((l) => l.descriptionZh !== undefined || l.colorZh !== undefined);
  const unique = lots.filter((l, i) => lots.findIndex((x) => x.lotNumber === l.lotNumber && x.productCode === l.productCode) === i);
  const labelSize = normalizeLabelSize(settings?.labelPrinter ?? {});
  const count = Number(copies);
  const valid = /^\d+$/.test(copies) && Number.isInteger(count) && count >= 1 && count <= 99;
  const today = new Date().toLocaleDateString("en-GB", { timeZone: VN_TIME_ZONE });
  const fits = (barcode: string) => "size" in labelSize && labelBarcodeFits(labelSize.size, barcode);
  const contents: LabelContent[] = unique.map((l) => ({
    productRef: labelRef(l.productCode, l.subRef), ...labelTexts(l, bilingual ? language : "en"), size: l.size,
    lotNumber: l.lotNumber, barcode: lotBarcode(l.productCode, l.lotNumber, fits), count: valid ? count : 1, footer: `Hisense Button  ${today}`,
  }));
  const unreadable = unique.filter((_, i) => !contents[i]!.barcode).map((l) => l.lotNumber);
  const barcodesFit = "size" in labelSize && contents.every((c) => labelBarcodeFits(labelSize.size, c.barcode));
  const ready = !!unique.length && valid && barcodesFit && !("invalid" in labelSize) && !isLoading && !settingsError;
  return <>
    <Button variant="secondary" size={size} disabled={disabled || !unique.length} onClick={() => { setCopies("1"); setError(false); setOpen(true); }}>
      <Tag className="size-4" aria-hidden="true" /> {t("lotLabels.button", { count: unique.length })}
    </Button>
    {open && <Modal title={t("lotLabels.title")} closeLabel={t("common.close")} onClose={() => setOpen(false)}>
      <div className="min-h-0 space-y-4 overflow-auto p-5">
        <p className="text-sm text-ink-soft">{t("lotLabels.intro")}</p>
        <div className="max-w-xs">
          <FieldLabel htmlFor="lot-label-copies">{t("lotLabels.copies")}</FieldLabel>
          <Input id="lot-label-copies" inputMode="numeric" value={copies} onChange={(e) => setCopies(e.target.value)} invalid={!valid} />
        </div>
        {!valid && <p role="alert" className="text-sm text-danger">{t("lotLabels.invalidCopies")}</p>}
        <p className="font-mono tabular text-sm">{t("lotLabels.total", { lots: unique.length, labels: valid ? count * unique.length : 0 })}</p>
        {bilingual && <LabelLanguageToggle value={language} onChange={setLanguage} />}
        {"invalid" in labelSize ? <p role="alert" className="text-sm text-danger">{t("label.error.badSize", { field: labelSize.invalid })}</p> : <>
          {!barcodesFit && <p role="alert" className="text-sm text-danger">{t("lotLabels.barcodeTooWide")}</p>}
          {unreadable.length > 0 && <p role="alert" className="text-sm text-warn">{t("lotLabels.unreadable", { lots: unreadable.join(", ") })}</p>}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">{contents.map((content, i) => <LabelPreview key={`${unique[i]!.productCode}|${unique[i]!.lotNumber}`} size={labelSize.size} content={content} />)}</div>
        </>}
        {(error || settingsError) && <p role="alert" className="text-sm text-danger">{t("lotLabels.printError")}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-line bg-surface p-4">
        <Button variant="secondary" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
        <Button disabled={!ready} onClick={() => {
          if (!ready || !("size" in labelSize)) return;
          try { printHtml(buildLabelDocument(labelSize.size, contents)); setOpen(false); } catch { setError(true); }
        }}>{t("lotLabels.print")}</Button>
      </div>
    </Modal>}
  </>;
}
